import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type ConsentType = 'terms' | 'privacy' | 'marketing' | 'call_recording' | 'analytics' | 'contacts_access' | 'media_sharing';

interface Consent {
  id: string;
  consent_type: ConsentType;
  version: string;
  granted: boolean;
  granted_at: string | null;
  revoked_at: string | null;
}

const CURRENT_VERSIONS: Record<ConsentType, string> = {
  terms: 'v1.0',
  privacy: 'v1.0',
  marketing: 'v1.0',
  call_recording: 'v1.0',
  analytics: 'v1.0',
  contacts_access: 'v1.0',
  media_sharing: 'v1.0',
};

const LOCAL_CACHE_KEY = 'elefa_privacy_consents';
const TRUST_DURATION_DAYS = 90;

interface CachedConsents {
  consents: Consent[];
  cachedAt: string;
  userId: string;
}

function getCachedConsents(userId: string): Consent[] | null {
  try {
    const raw = localStorage.getItem(LOCAL_CACHE_KEY);
    if (!raw) return null;
    const cached: CachedConsents = JSON.parse(raw);
    if (cached.userId !== userId) return null;
    const cachedDate = new Date(cached.cachedAt);
    const expiryMs = TRUST_DURATION_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - cachedDate.getTime() > expiryMs) return null;
    return cached.consents;
  } catch {
    return null;
  }
}

function setCachedConsents(userId: string, consents: Consent[]) {
  const cached: CachedConsents = { consents, cachedAt: new Date().toISOString(), userId };
  localStorage.setItem(LOCAL_CACHE_KEY, JSON.stringify(cached));
}

export function usePrivacyConsent() {
  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchConsents = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      setUserId(user.id);

      // Check local cache first - trust device for 90 days
      const cached = getCachedConsents(user.id);
      if (cached) {
        setConsents(cached);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('privacy_consents')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        console.error('Failed to fetch consents:', error);
      } else {
        const fetched = (data as Consent[]) || [];
        setConsents(fetched);
        setCachedConsents(user.id, fetched);
      }
      setLoading(false);
    };

    fetchConsents();
  }, []);

  const hasConsent = useCallback((type: ConsentType): boolean => {
    const consent = consents.find(
      c => c.consent_type === type && 
           c.version === CURRENT_VERSIONS[type] && 
           c.granted && 
           !c.revoked_at
    );
    return !!consent;
  }, [consents]);

  const needsConsent = useCallback((type: ConsentType): boolean => {
    return !hasConsent(type);
  }, [hasConsent]);

  const grantConsent = useCallback(async (type: ConsentType): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { data, error } = await supabase
        .from('privacy_consents')
        .upsert({
          user_id: userId,
          consent_type: type,
          version: CURRENT_VERSIONS[type],
          granted: true,
          granted_at: new Date().toISOString(),
          revoked_at: null,
        }, {
          onConflict: 'user_id,consent_type,version',
        })
        .select()
        .single();

      if (error) throw error;

      setConsents(prev => {
        const filtered = prev.filter(
          c => !(c.consent_type === type && c.version === CURRENT_VERSIONS[type])
        );
        const updated = [...filtered, data as Consent];
        if (userId) setCachedConsents(userId, updated);
        return updated;
      });

      return true;
    } catch (error) {
      console.error('Failed to grant consent:', error);
      toast({
        title: 'Error',
        description: 'Failed to save your consent preference',
        variant: 'destructive',
      });
      return false;
    }
  }, [userId, toast]);

  const revokeConsent = useCallback(async (type: ConsentType): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { data, error } = await supabase
        .from('privacy_consents')
        .update({
          granted: false,
          revoked_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .eq('consent_type', type)
        .eq('version', CURRENT_VERSIONS[type])
        .select()
        .single();

      if (error) throw error;

      setConsents(prev => {
        const updated = prev.map(c => 
          c.consent_type === type && c.version === CURRENT_VERSIONS[type]
            ? { ...c, granted: false, revoked_at: new Date().toISOString() }
            : c
        );
        if (userId) setCachedConsents(userId, updated);
        return updated;
      });

      toast({
        title: 'Consent Revoked',
        description: `Your ${type} consent has been revoked`,
      });

      return true;
    } catch (error) {
      console.error('Failed to revoke consent:', error);
      toast({
        title: 'Error',
        description: 'Failed to update your consent preference',
        variant: 'destructive',
      });
      return false;
    }
  }, [userId, toast]);

  const getConsentStatus = useCallback((type: ConsentType) => {
    const consent = consents.find(
      c => c.consent_type === type && c.version === CURRENT_VERSIONS[type]
    );
    
    return {
      granted: consent?.granted && !consent?.revoked_at,
      grantedAt: consent?.granted_at,
      revokedAt: consent?.revoked_at,
      version: CURRENT_VERSIONS[type],
    };
  }, [consents]);

  return {
    loading,
    consents,
    hasConsent,
    needsConsent,
    grantConsent,
    revokeConsent,
    getConsentStatus,
    CURRENT_VERSIONS,
  };
}
